import { useRef, useState, useMemo, useEffect, type ChangeEvent } from 'react'
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Grid,
  GridItem,
  InputLeftAddon
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { StatCard} from '@/components/dashboard'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { apiClient } from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'
import { Sun, Moon, Wallet, TrendingDown, Landmark, ArrowRightLeft } from 'lucide-react'

type TabValue = 'all' | 'trash'

export default function BudgetManagementPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [modalMessage, setModalMessage] = useState('')

  // Date From and Date To
  const today = new Date()
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  const formatDate = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  const [dateFrom, setDateFrom] = useState<string>(formatDate(startOfMonth))
  const [dateTo, setDateTo] = useState<string>(formatDate(endOfMonth))
  const initialDateFrom = useRef(formatDate(startOfMonth))
  const initialDateTo = useRef(formatDate(endOfMonth))
  // Expense Type
  const [expenseType, setExpenseType] = useState<string>('All')
  const expenseTypeOptions = [
    { value: 'All', label: 'All Expenses' },
    { value: 'Issued Budget', label: 'Issued Budget' },
    { value: 'Truck Expense', label: 'Truck Expense' },
    { value: 'Parts Expense', label: 'Parts Expense' },
    { value: 'Other Expense', label: 'Other Expense' },
    { value: 'Driver Cash Advance', label: 'Driver Cash Advance' },
    { value: 'Helper Cash Advance', label: 'Helper Cash Advance' },
  ]
  // Stat Cards
  const [stats, setStats] = useState({
    total_budget: 0,
    total_expense: 0,
    cash_on_hand: 0,
    total_carry_over_coh: 0,
  });
  // Shift
  type dayNightMode = 'Day' | 'Night'
  const [dayNightMode, setMode] = useState<dayNightMode>('Day')
  const access = useAccessCheck()
  const statusFilterOptions = useMemo(
    () => [
      { value: 'Active', label: 'Active' },
      { value: 'Inactive', label: 'Inactive' },
    ],
    []
  )

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        type: {
          name: 'Expense Type',
          // withSort: true,
          maxWidth: '180px',
        },

        description: {
          name: 'Description',
          maxWidth: '300px',
        },

        amount: {
          name: 'Amount',
          // withSort: true,
          maxWidth: '150px',
        },

        shift: {
          name: 'Shift',
          maxWidth: '120px',
        },

        transaction_date: {
          name: 'Transaction Date',
          // withSort: true,
          maxWidth: '180px',
        },
      },
        dataSource: activeTab === 'trash' ? '/budget/summary' : '/budget/summary',
        primaryKey: 'id',
        softDelete: true,
        displayInModal: true,
        noRecordText: 'No Budget Records found',
      }),
    [activeTab, statusFilterOptions]
  )

  const statCards = [
    {
      title: 'Total Budget',
      value: stats.total_budget,
      icon: Wallet,
      color: 'green',
    },
    {
      title: 'Total Expense',
      value: stats.total_expense,
      icon: TrendingDown,
      color: 'red',
    },
    {
      title: 'Cash on Hand',
      value: stats.cash_on_hand,
      icon: Landmark,
      color: 'blue',
    },
    {
      title: 'Cash on Hand (Previous Shift)',
      value: stats.total_carry_over_coh,
      icon: ArrowRightLeft,
      color: 'purple',
    },
  ];

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: false,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: false,
      can_import: false,
    }),
    [access]
  )

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const tableParams = useMemo(() => {
    const params = {
      search: searchQuery,
      shift: dayNightMode,
      type: expenseType === 'All' ? 'All' : expenseType,
      transaction_date_from: dateFrom,
      transaction_date_to: dateTo,
    };
    return params;
  }, [searchQuery, dayNightMode, expenseType, dateFrom, dateTo]);

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) {
      searchRef.current.value = ''
    }
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    const query = searchRef.current?.value || ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const fetchBudgetStats = async () => {
        try {
          const response = await apiClient.get('/budget/summary', {
            params: tableParams,
          });
          const data = response.data;

          setStats({
            total_budget: data.total_budget,
            total_expense: data.total_expense,
            cash_on_hand: data.cash_on_hand,
            total_carry_over_coh: data.total_carry_over_coh ?? 0,
          });
        } catch (error) {
          console.error('Error fetching stats', error);
        }
      };

  useEffect(() => {
    fetchBudgetStats()
  }, [tableParams])

  const convertExpenseType = (text: string) => {
    return text.toLowerCase().replace(/\s+/g, '-');
  };
  
  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        {/* Stat Cards */}
          <Box pb={2} flexShrink={0} overflowX="auto" overflowY="hidden">
            <Grid
              templateColumns={`repeat(${statCards.length}, minmax(220px, 1fr))`}
              gap={{ base: 3, md: 6 }}
              w="max-content"
              minW="100%"
              sx={{
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin',
              }}
            >
              {statCards.map((stat, index) => (
                <GridItem key={index}>
                  <Box
                    sx={{
                      '.chakra-card__body': {
                        padding: { base: 3, md: 5 },
                      },
                      '.chakra-text': {
                        fontSize: { base: 'xs', md: 'sm' },
                      },
                      '.chakra-stat__number': {
                        fontSize: { base: 'xl', md: '2xl' },
                      },
                      'svg': {
                        width: '1.5rem',
                        height: '1.5rem',
                      },
                      '@media (min-width: 48em)': {
                        'svg': {
                          width: '2rem',
                          height: '2rem',
                        },
                      },
                    }}
                  >
                    <StatCard
                      title={stat.title}
                      value={stat.value}
                      icon={stat.icon}
                      color={stat.color}
                    />
                  </Box>
                </GridItem>
              ))}
            </Grid>
          </Box>
        

        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <HStack justify="space-between" align="center" spacing={4}>
                <VStack align="flex-start" spacing={0}>
                  <Heading size="md" mb={1}>
                    Budgets
                  </Heading>
                  <Text fontSize="sm" color={colors.text}>
                    Manage Budget records
                  </Text>
                </VStack>

                {/* Day/Night Mode Toggle */}
                <HStack spacing={4}>
                  {/* Double Button Toggle  */}
                  <Button
                    size="sm"
                    leftIcon={<Sun size={16} />}
                    variant={dayNightMode === 'Day' ? 'solid' : 'outline'}
                    onClick={() => setMode('Day')}
                  >
                    Day
                  </Button>

                  <Button
                    size="sm"
                    leftIcon={<Moon size={16} />}
                    variant={dayNightMode === 'Night' ? 'solid' : 'outline'}
                    onClick={() => setMode('Night')}
                  >
                    Night
                  </Button>

                </HStack>
              </HStack>
            </CardHeader>

            

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                    <TabList borderBottom="none">
                      <Tab>
                        All{' '}
                        <Badge ml={2} colorScheme="blue">
                          {totalRows}
                        </Badge>
                      </Tab>
                      {totalTrash > 0 && (
                        <Tab>
                          Trash{' '}
                          <Badge ml={2} colorScheme="red">
                            {totalTrash}
                          </Badge>
                        </Tab>
                      )}
                    </TabList>
                  </Tabs>
                  <Box pb={2}>
                    <ActionButtons
                      pathname="/budget-management"
                      createLabel="Create New Budget Record"
                    />
                  </Box>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack
                spacing={6}
                flexWrap="nowrap"
                overflowX="auto"
                overflowY="hidden"
                sx={{
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'thin',
                }}
              >
                {/* Expense Type Selection */}
                <HStack spacing={4} align="center" flexShrink={0}>
                  <HStack spacing={1}>
                    <Select
                      size="sm"
                      maxW="220px"
                      value={expenseType}
                      onChange={(e) => setExpenseType(e.target.value)}
                      >
                      {expenseTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </HStack>
                </HStack>

                {/* Date Range */}
                <HStack spacing={4} align="center" flexShrink={0}>
                  {/* From */}
                  <HStack spacing={1}>
                    <InputGroup size="sm" maxW="220px" minW="150px">
                      <InputLeftAddon fontSize="sm">
                        From
                      </InputLeftAddon>
                      <Input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        maxW="150px"
                        minW="100px"
                      />
                    </InputGroup>
                  </HStack>
                
                  {/* To */}    
                  <HStack spacing={1}>
                    <InputGroup size="sm" maxW="220px" minW="150px">
                      <InputLeftAddon fontSize="sm">
                        To:
                      </InputLeftAddon>
                      <Input
                        type="date"
                        value={dateTo}
                          onChange={(e) => setDateTo(e.target.value)}
                        maxW="150px"
                        minW="100px"
                      />
                    </InputGroup>
                  </HStack>

                  {(dateFrom !== initialDateFrom.current || dateTo !== initialDateTo.current) && (
                    <Button
                      size="sm"
                      variant="outline"
                      borderColor={colors.border}
                      bg={colors.inputBg}
                      color={colors.text}
                      onClick={() => {
                        setDateFrom(initialDateFrom.current)
                        setDateTo(initialDateTo.current)
                      }}
                      >
                      Reset
                    </Button>
                  )}
                </HStack>
      

                <Box flex="1 1 280px" minW="150px" maxW="280px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={{...tableOptions,
                    showCheckboxes: false,
                  }}
                  params={tableParams}
                  access={permissions}
                  setSubSub={showSubSubCallback}
                  onOpenModal={(row) => {
                    const sourceId = row?.source_id
                    if (sourceId == null || sourceId === '') {
                      showWarning(
                        'Cannot open editor',
                        'This row has no source record id; it cannot be edited.'
                      )
                      return
                    }
                    const expenseSlug = convertExpenseType(String(row?.type ?? ''))
                    navigate(`/budget-management/${expenseSlug}/${sourceId}`)
                  }}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody>
            <Text>{modalMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}
